import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react'

import { Button, Flex } from 'antd'
import Input from 'govwell-ui/components/Input/Input'
import styled from 'styled-components'
import { AIChatCitationFragment, AIChatResponseEventFragment } from 'types/graphql'
import { StylePropsMobile } from 'web/src/components/shared/StyledComponents'

import { useCreateAIChatMutation, useSubscribeToAIChatSessionResponses } from 'src/fetch/aiChat'

import { WindowPostMessage, WindowPostMessageKey } from '../../constants'
import { useGetOrganizationForAIChatWindowQuery } from '../../fetch/organizations'
import { Header, Icon, Link, T } from '../../govwell-ui'
import { useChatStorageIds } from '../../hooks/use-chat-storage-ids'
import { useCurrentBreakpoint } from '../../hooks/use-current-breakpoint'
import { useIsInIFrame } from '../../hooks/use-is-in-iframe'
import { useMergeRefs } from '../../hooks/use-merge-refs'

import AIChatLanguageSupportRow from './AIChatLanguageSupportRow'
import AIChatTitle from './AIChatTitle'
import Message from './AIResponseMessage/Message'
import { ScrollableMessages } from './ChatStyles'

const EXPAND_TRANSITION_TIME = 300

type AIChatState = {
  text: string
  sender: 'user' | 'ai'
  id?: number
  questionCreatedAt?: string
  responseCreatedAt?: string
  isThinking?: boolean
  citations?: AIChatCitationFragment[]
}

// duplicated in api/src/services/aiChats/aiCreateFAQ.ts
const BaseSamplePrompts = [
  'Help me find the right form',
  'What things can you help me with?',
  'Look up a record',
  'Ask questions about codes',
  'Estimate fees for my project',
]

const ChatContainer = styled.section<
  StylePropsMobile & {
    $isExpanded: boolean
    $maxHeight?: React.CSSProperties['maxHeight']
    $width?: React.CSSProperties['width']
  }
>`
  position: relative;
  width: ${(props) =>
    props.$width
      ? props.$width
      : props.$isExpanded
        ? `${100 * (2 / 3)}%`
        : props.$small
          ? '100%'
          : 'min(90%, 600px)'}; /* Ensures proper resizing for screens < 700px */

  max-width: ${(props) => (props.$small ? 'calc(100vw - 32px)' : 'calc(100vw - 70px)')};
  max-height: ${(props) =>
    props.$maxHeight ? props.$maxHeight : props.$isExpanded ? '70vh' : '400px'};
  padding: ${(props) => (props.$small ? '12px' : '16px')};
  border: 1px solid #ccc;
  border-radius: 8px;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  gap: 8px;
  box-sizing: border-box;
  transition:
    width ${EXPAND_TRANSITION_TIME}ms ease,
    max-width ${EXPAND_TRANSITION_TIME}ms ease,
    height ${EXPAND_TRANSITION_TIME}ms ease,
    margin ${EXPAND_TRANSITION_TIME}ms ease;
  overflow: hidden; /* Ensures messages don't exceed container */
`

const updateLastMessageAction = (
  update: (message: AIChatState) => AIChatState
): SetStateAction<AIChatState[]> => {
  return (prev) => {
    const lastMessage = prev[prev.length - 1]
    if (!lastMessage) {
      return prev
    }
    return [...prev.slice(0, -1), update(lastMessage)]
  }
}

type Props = {
  orgSlug: string
  inputRef?: React.RefObject<HTMLInputElement>
  isExpanded?: boolean
  maxHeight?: React.CSSProperties['maxHeight']
  setIsExpanded?: Dispatch<SetStateAction<boolean>>
  showHeader?: boolean
  width?: React.CSSProperties['width']
}

const AIAssistantChatWindow = ({
  orgSlug,
  inputRef,
  isExpanded,
  maxHeight,
  setIsExpanded,
  showHeader,
  width,
}: Props) => {
  const localInputRef = useRef<HTMLInputElement>(null)
  const mergedInputRef = useMergeRefs(localInputRef, ...(inputRef ? [inputRef] : []))
  const [userTextInput, setuserTextInput] = useState('')
  const [messages, setMessages] = useState<AIChatState[]>([])
  const [isAssistantTyping, setIsAssistantTyping] = useState(false)
  const { mutateAsync: createAIChat } = useCreateAIChatMutation()
  const chatContainerRef = useRef<HTMLDivElement>(null)
  const chatRef = useRef<HTMLDivElement>(null)
  const { chatLocalStorageId, chatSessionStorageId } = useChatStorageIds()
  const { organization } = useGetOrganizationForAIChatWindowQuery(orgSlug)
  const city = organization?.city || ''

  const samplePrompts = BaseSamplePrompts

  const onResponseEvent = useCallback((event: AIChatResponseEventFragment) => {
    if (event.__typename === 'AIChatTextUpdate') {
      setMessages(
        updateLastMessageAction((lastMessage) => {
          return {
            ...lastMessage,
            text: event.fullText,
            isThinking: !event.done,
          }
        })
      )
    } else if (event.__typename === 'AIChatTextDelta') {
      setMessages(
        updateLastMessageAction((lastMessage) => {
          return {
            ...lastMessage,
            text: lastMessage.text + (event.text || ''),
            isThinking: !event.done,
          }
        })
      )
    }
  }, [])
  useSubscribeToAIChatSessionResponses({
    chatSessionStorageId,
    onResponseEvent,
  })

  const isInIFrame = useIsInIFrame()
  const [sourceUrl, setSourceUrl] = useState<string>()
  useEffect(() => {
    if (!isInIFrame) {
      setSourceUrl(window.location.href)
      return
    }
    window.parent.postMessage(
      {
        messageKey: WindowPostMessageKey.AIChatReadyToReceiveSourceUrl,
      },
      {
        targetOrigin: '*',
      }
    )

    const listener = (e: MessageEvent<WindowPostMessage<WindowPostMessageKey.AIChatSourceUrl>>) => {
      if (e.data.messageKey === WindowPostMessageKey.AIChatSourceUrl) {
        setSourceUrl(e.data.payload.sourceUrl)
      }
    }
    window.addEventListener('message', listener)
  }, [isInIFrame])

  const { isSmallScreen } = useCurrentBreakpoint()
  const isSmall = isSmallScreen()
  const [hasBeenExpandedAfterFirstMessage, setHasBeenExpandedAfterFirstMessage] = useState(false)

  useEffect(() => {
    if (messages.length > 0 && !hasBeenExpandedAfterFirstMessage && setIsExpanded) {
      setHasBeenExpandedAfterFirstMessage(true)
      setIsExpanded?.(true)
    }
  }, [isExpanded, messages.length, hasBeenExpandedAfterFirstMessage, setIsExpanded])

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
    }
  }, [messages])

  const handleAIChat = useCallback(
    async (autoSendText?: string) => {
      const textMessage = autoSendText ?? userTextInput
      if (!orgSlug || !textMessage.trim()) {
        localInputRef.current?.focus()
        return
      }

      setMessages((prev) => {
        return [
          ...prev,
          { text: textMessage, sender: 'user', questionCreatedAt: new Date().toISOString() },
          { text: '', sender: 'ai', isThinking: true },
        ]
      })
      setuserTextInput('')
      setIsAssistantTyping(true)

      try {
        const answer = await createAIChat({
          input: {
            chatLocalStorageId,
            chatSessionStorageId,
            orgSlug,
            question: textMessage,
            source: isInIFrame ? 'IFrame' : 'PublicPortal',
            sourceUrl,
          },
        })
        setMessages(
          updateLastMessageAction((lastMessage) => {
            return {
              ...lastMessage,
              questionCreatedAt: answer.createAIChat.questionCreatedAt ?? '',
              responseCreatedAt: answer.createAIChat.responseCreatedAt ?? '',
              id: answer.createAIChat.id,
              isThinking: false,
              ...(answer.createAIChat.response ? { text: answer.createAIChat.response } : {}),
              ...(answer.createAIChat.citations?.length
                ? { citations: answer.createAIChat.citations }
                : {}),
            }
          })
        )
      } catch (e) {
        console.error(e)
        setMessages(
          updateLastMessageAction((lastMessage) => {
            const now = new Date().toISOString()
            return {
              ...lastMessage,
              text: `<div>Sorry, I don't know the answer to that question. Please try again or ask a different question.</div>`,
              questionCreatedAt: now,
              responseCreatedAt: now,
              isThinking: false,
            }
          })
        )
      } finally {
        setIsAssistantTyping(false)
      }
    },
    [
      userTextInput,
      orgSlug,
      createAIChat,
      chatLocalStorageId,
      chatSessionStorageId,
      isInIFrame,
      sourceUrl,
      setIsAssistantTyping,
    ]
  )

  // `void` explicitly discards the promise returned by `handleAIChat` (an async function).
  // This satisfies ESLint's `no-floating-promises` rule without needing to `await` it,
  // since the callback signature must stay synchronous for the click handler.
  const handleFollowUpClick = useCallback(
    (prompt: string) => {
      void handleAIChat(prompt)
    },
    [handleAIChat]
  )

  const toggleExpand = () => {
    setIsExpanded?.((prev) => {
      const expanding = !prev

      setTimeout(() => {
        if (chatRef.current) {
          chatRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
        if (!expanding) {
          if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
          }
        }
      }, EXPAND_TRANSITION_TIME)

      return expanding
    })
  }

  if (!orgSlug) {
    return null
  }

  return (
    <ChatContainer
      $isExpanded={isExpanded ?? false}
      $maxHeight={maxHeight}
      $small={isSmall}
      $width={width}
      ref={chatRef}
    >
      {messages.length > 0 && (
        <>
          {showHeader && (
            <div>
              <Header
                title={<AIChatTitle city={city} />}
                actions={
                  <Flex align="center" gap="8px">
                    <AIChatLanguageSupportRow />
                    {!setIsExpanded || isSmall ? null : (
                      <T.Body>
                        <Link onClick={toggleExpand}>
                          {isExpanded ? <Icon.FaMinimize /> : <Icon.FaMaximize />}
                        </Link>
                      </T.Body>
                    )}
                  </Flex>
                }
              />
            </div>
          )}
          <ScrollableMessages ref={chatContainerRef} isExpanded={isExpanded}>
            {messages.map((msg, index) => {
              return (
                <Message
                  key={msg.id ?? `msg-${index}`}
                  handleAIChat={handleFollowUpClick}
                  html={msg.text}
                  index={index}
                  messageId={msg.id}
                  sender={msg.sender}
                  showToolbar
                  isThinking={msg.isThinking}
                  citations={msg.citations}
                />
              )
            })}
          </ScrollableMessages>
        </>
      )}

      <Flex align="center" gap="8px">
        <Input
          ariaLabel="Message to send to the Community Assistant"
          autoFocus
          id="ai-chat"
          onKeyDown={(e) => e.key === 'Enter' && !isAssistantTyping && handleAIChat()}
          onValueChange={setuserTextInput}
          placeholder={messages.length > 0 ? 'Type here...' : 'Type here or select below...'}
          ref={mergedInputRef}
          value={userTextInput}
          width="100%"
        />
        {city && (
          <Button
            type="primary"
            onClick={() => handleAIChat()}
            disabled={isAssistantTyping}
            icon={messages.length > 0 ? <Icon.FaSend /> : <Icon.FaMessageQuestion />}
          >
            {messages.length === 0 ? `Ask ${city} AI` : null}
          </Button>
        )}
      </Flex>
      {messages.length === 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '8px',
            }}
          >
            {samplePrompts.map((text, index) => (
              <Button
                key={index}
                type="default"
                onClick={() => handleAIChat(text)}
                style={{
                  whiteSpace: 'normal',
                  height: 'auto',
                  textAlign: 'left',
                  maxWidth: '100%',
                }}
              >
                {text}
              </Button>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <AIChatLanguageSupportRow />
          </div>
        </div>
      )}
    </ChatContainer>
  )
}

export default AIAssistantChatWindow
